interface PubSubMessage {
  name: string;
  args?: Record<string, any>;
}

interface ListenerRegistration {
  eventCenter: any;
  listenerId: any;
}

const PUB_SUB_APIS = new Set([
  'subscribe',
  'unsubscribe',
  'publish',
  'subscribeForSubId',
  'unsubscribeWithSubId',
]);
const PUB_SUB_PARAMS: Record<string, Set<string>> = {
  subscribe: new Set(['scope', 'eventName', 'handle']),
  unsubscribe: new Set(['scope', 'eventName', 'handle']),
  publish: new Set(['scope', 'eventName', 'data', 'supportMultiProcess']),
  subscribeForSubId: new Set(['scope', 'eventName', 'handle']),
  unsubscribeWithSubId: new Set(['scope', 'eventName', 'handle', 'subId']),
};
const listeners = new Map<string, ListenerRegistration>();

export function isBrowserPubSubAPI(name: string): boolean {
  return PUB_SUB_APIS.has(name);
}

function getEventCenter(mach: any): any {
  const eventCenter = mach?.requireModule?.('EventCenter');
  if (
    typeof eventCenter?.addListener !== 'function'
    || typeof eventCenter?.removeListener !== 'function'
    || typeof eventCenter?.emit !== 'function'
  ) {
    throw new Error('KSI browser EventCenter is unavailable');
  }
  return eventCenter;
}

function getEventIdentity(args: Record<string, any>): {
  scope: string;
  eventName: string;
  key: string;
  channel: string;
} {
  const scope = typeof args.scope === 'undefined' ? 'default' : args.scope;
  const { eventName } = args;
  if (typeof scope !== 'string') throw new TypeError('[ksi]: scope must be a string');
  if (typeof eventName !== 'string' || !eventName) {
    throw new TypeError('[ksi]: eventName must be a non-empty string');
  }
  const key = `${scope}\u0000${eventName}`;
  const channel = scope === 'default' ? eventName : `__ksi__${key}`;
  return {
    scope, eventName, key, channel,
  };
}

function sendPublishEvent(mach: any, scope: string, eventName: string, data: any): void {
  if (typeof mach?.sendEvent !== 'function') {
    throw new Error('KSI browser event transport is unavailable');
  }
  const eventData = {
    type: 'event',
    scope: 'default',
    name: 'onPublish',
    data: {
      scope,
      eventName,
      data: data && typeof data === 'object' && !Array.isArray(data) ? data : {},
    },
    metrics: {},
  };
  mach.sendEvent('ksi.event', { msg: JSON.stringify(eventData) });
}

export function canHandleBrowserPubSub(name: string, mach: any): boolean {
  if (!isBrowserPubSubAPI(name) || typeof mach?.sendEvent !== 'function') return false;
  try {
    getEventCenter(mach);
    return true;
  } catch (error) {
    return false;
  }
}

export function canUseBrowserPubSub(
  name: string,
  mode: string,
  fields: string[],
  mach: any,
): boolean {
  if (!canHandleBrowserPubSub(name, mach)) return false;
  if (!mode) return true;
  if (mode === 'success') return fields.length === 0;
  return mode === 'param'
    && fields.length === 1
    && Boolean(PUB_SUB_PARAMS[name]?.has(fields[0]));
}

export default function invokeBrowserPubSub(message: PubSubMessage, mach: any): Record<string, any> {
  const args = message.args || {};
  const {
    scope, eventName, key, channel,
  } = getEventIdentity(args);
  const eventCenter = getEventCenter(mach);

  if (message.name === 'subscribe') {
    if (!listeners.has(key)) {
      const listenerId = eventCenter.addListener(channel, (data: any) => {
        sendPublishEvent(mach, scope, eventName, data);
      });
      listeners.set(key, { eventCenter, listenerId });
    }
    return {};
  }

  if (message.name === 'unsubscribe') {
    const registration = listeners.get(key);
    if (registration) {
      registration.eventCenter.removeListener(registration.listenerId);
      listeners.delete(key);
    }
    return {};
  }

  if (message.name === 'publish') {
    const data = args.data && typeof args.data === 'object' && !Array.isArray(args.data)
      ? args.data
      : {};
    eventCenter.emit(channel, data);
    return {};
  }

  throw new Error(`KSI pub/sub API ${message.name} is not directly invokable`);
}
